import { useCallback, useEffect, useRef, useState } from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';

import { uploadOrdainedFile } from '../../api/ordained';
import Button from '../v2/Button/Button';
import { Typography } from '../v2/Typography/Typography';

const ONE_MB = 1_048_576;
const ACCEPTED_MIME = {
  'application/pdf': ['.pdf'],
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
};

export type FileUploadChange =
  | { url: string; filename: string }
  | { file: File; filename: string }
  | null;

type Props = {
  value: string | null;
  filename: string | null;
  /** When set, the selected file is held locally until upload on submit. */
  pendingFile?: File | null;
  deferUpload?: boolean;
  onChange: (next: FileUploadChange) => void;
};

type Phase = 'idle' | 'uploading' | 'error';

const humanReadable = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < ONE_MB) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / ONE_MB).toFixed(2)} MB`;
};

const FileUpload = ({
  value,
  filename,
  pendingFile = null,
  deferUpload = false,
  onChange,
}: Props) => {
  const [phase, setPhase] = useState<Phase>('idle');
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Abort any in-flight upload when the component unmounts so its resolution
  // can't call setState on a torn-down tree.
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  const uploadFile = useCallback(
    async (file: File) => {
      // Cancel any prior upload before kicking off a new one (e.g. user picks
      // a second file while the first is still uploading).
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setPhase('uploading');
      setError(null);
      try {
        const resp = await uploadOrdainedFile(file, controller.signal);
        if (controller.signal.aborted) return;
        if (!resp.success || !resp.data?.url) {
          throw new Error(resp.message || 'Upload failed');
        }
        onChange({ url: resp.data.url, filename: file.name });
        setPhase('idle');
      } catch (e) {
        if (controller.signal.aborted) return;
        const msg = e instanceof Error ? e.message : 'Upload failed';
        setError(msg);
        setPhase('error');
      }
    },
    [onChange]
  );

  const onDrop = useCallback(
    (accepted: File[], rejected: FileRejection[]) => {
      if (rejected.length > 0) {
        const rejection = rejected[0];
        const code = rejection.errors[0]?.code;
        if (code === 'file-too-large') {
          setError('File is too large. Max size is 1MB.');
        } else if (code === 'file-invalid-type') {
          setError('Unsupported file type. Use PDF, PNG, or JPEG.');
        } else {
          setError(rejection.errors[0]?.message || 'File rejected');
        }
        setPhase('error');
        return;
      }
      const file = accepted[0];
      if (!file) return;
      if (deferUpload) {
        setError(null);
        setPhase('idle');
        onChange({ file, filename: file.name });
        return;
      }
      void uploadFile(file);
    },
    [deferUpload, onChange, uploadFile]
  );

  const hasFile = deferUpload ? !!value || !!pendingFile : !!value;
  const displayName = pendingFile?.name ?? filename;

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept: ACCEPTED_MIME,
    maxSize: ONE_MB,
    multiple: false,
    noClick: hasFile,
    noKeyboard: hasFile,
  });

  return (
    <div className='w-full'>
      <div
        {...getRootProps()}
        className={`p-6 border border-dashed rounded-lg text-center ${
          hasFile ? 'cursor-default' : 'cursor-pointer'
        } ${
          isDragActive
            ? 'border-forest-primary bg-cosmicLatte-dark'
            : 'border-forest-100'
        }`}
      >
        <input {...getInputProps()} />
        {phase === 'uploading' ? (
          <Typography type='body-400' variant='functional-high'>
            Uploading…
          </Typography>
        ) : hasFile ? (
          <div className='flex flex-col items-center gap-2'>
            <Typography type='body-400' variant='functional-high'>
              {displayName || 'Selected file'}
            </Typography>
            <Typography type='body-200' variant='functional-low'>
              {deferUpload && pendingFile
                ? 'Will upload when you submit'
                : 'Uploaded successfully'}
            </Typography>
            <div className='flex flex-row gap-3 mt-2'>
              <Button
                type='button'
                variant='text'
                eventClick={(e) => {
                  e.stopPropagation();
                  open();
                }}
              >
                Replace
              </Button>
              <Button
                type='button'
                variant='text'
                eventClick={(e) => {
                  e.stopPropagation();
                  onChange(null);
                  setError(null);
                  setPhase('idle');
                }}
              >
                Remove
              </Button>
            </div>
          </div>
        ) : (
          <div className='flex flex-col items-center gap-2'>
            <Typography type='body-400' variant='functional-high'>
              {isDragActive
                ? 'Drop your file here'
                : 'Drag and drop a file, or click to browse'}
            </Typography>
            <Typography type='body-200' variant='functional-low'>
              {`PDF, PNG, or JPEG — up to ${humanReadable(ONE_MB)}`}
            </Typography>
          </div>
        )}
      </div>
      {error && phase !== 'uploading' && (
        <Typography type='body-200' className='mt-2 text-red-600'>
          {error}
        </Typography>
      )}
    </div>
  );
};

export default FileUpload;
